import React, { useState } from 'react';
import { MachineData } from '../../types/types';
import { formatNumber } from '../../utils/formatters';
import { ArrowDown, ArrowUp, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
interface MachineComparisonTableProps {
  machineData: MachineData[];
}
type SortField = 'name' | 'efficiency' | 'load' | 'warnings' | 'throughput' | 'errorRate';
type SortDirection = 'asc' | 'desc';
export const MachineComparisonTable: React.FC<MachineComparisonTableProps> = ({
  machineData
}) => {
  const [sortField, setSortField] = useState<SortField>('efficiency');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  // Berechne den Durchsatz für jede Maschine
  const calculateThroughput = (machine: MachineData) => {
    const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
    return Math.round(baseRate * 60);
  };
  // Sortiere die Maschinendaten
  const sortedMachineData = [...machineData].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'efficiency':
        comparison = a.metrics.efficiency - b.metrics.efficiency;
        break;
      case 'load':
        comparison = a.metrics.currentLoad - b.metrics.currentLoad;
        break;
      case 'warnings':
        comparison = a.metrics.warnings - b.metrics.warnings;
        break;
      case 'throughput':
        comparison = calculateThroughput(a) - calculateThroughput(b);
        break;
      case 'errorRate':
        comparison = a.analytics.errorRate - b.analytics.errorRate;
        break;
      default:
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Toggle Sortierung
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  // Render Sortierungspfeil
  const renderSortArrow = (field: SortField) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? <ArrowUp size={14} className="inline ml-1" data-id="element-1000" /> : <ArrowDown size={14} className="inline ml-1" data-id="element-1001" />;
  };
  // Render Statusindikator
  const renderStatusIndicator = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircle size={16} className="text-green-400" data-id="element-1002" />;
      case 'maintenance':
        return <AlertTriangle size={16} className="text-yellow-400" data-id="element-1003" />;
      case 'error':
        return <XCircle size={16} className="text-red-400" data-id="element-1004" />;
      default:
        return null;
    }
  };
  return <table className="w-full text-left" data-id="element-1005">
      <thead data-id="element-1006">
        <tr className="border-b border-gray-700" data-id="element-1007">
          <th className="pb-3 pr-6 text-gray-400" data-id="element-1008">
            <button className="flex items-center text-left font-medium focus:outline-none" onClick={() => handleSort('name')} data-id="element-1009">
              Maschine {renderSortArrow('name')}
            </button>
          </th>
          <th className="pb-3 pr-6 text-gray-400" data-id="element-1010">Status</th>
          <th className="pb-3 pr-6 text-gray-400" data-id="element-1011">
            <button className="flex items-center text-left font-medium focus:outline-none" onClick={() => handleSort('efficiency')} data-id="element-1012">
              Effizienz {renderSortArrow('efficiency')}
            </button>
          </th>
          <th className="pb-3 pr-6 text-gray-400" data-id="element-1013">
            <button className="flex items-center text-left font-medium focus:outline-none" onClick={() => handleSort('load')} data-id="element-1014">
              Auslastung {renderSortArrow('load')}
            </button>
          </th>
          <th className="pb-3 pr-6 text-gray-400" data-id="element-1015">
            <button className="flex items-center text-left font-medium focus:outline-none" onClick={() => handleSort('throughput')} data-id="element-1016">
              Durchsatz {renderSortArrow('throughput')}
            </button>
          </th>
          <th className="pb-3 pr-6 text-gray-400" data-id="element-1017">
            <button className="flex items-center text-left font-medium focus:outline-none" onClick={() => handleSort('warnings')} data-id="element-1018">
              Warnungen {renderSortArrow('warnings')}
            </button>
          </th>
          <th className="pb-3 text-gray-400" data-id="element-1019">
            <button className="flex items-center text-left font-medium focus:outline-none" onClick={() => handleSort('errorRate')} data-id="element-1020">
              Fehlerrate {renderSortArrow('errorRate')}
            </button>
          </th>
        </tr>
      </thead>
      <tbody data-id="element-1021">
        {sortedMachineData.map(machine => <tr key={machine.id} className="border-b border-gray-700/50" data-id="element-1022">
            <td className="py-3 pr-6 font-medium" data-id="element-1023">{machine.name}</td>
            <td className="py-3 pr-6" data-id="element-1024">
              <div className="flex items-center gap-2" data-id="element-1025">
                {renderStatusIndicator(machine.status)}
                <span className={`
                  ${machine.status === 'operational' ? 'text-green-400' : ''}
                  ${machine.status === 'maintenance' ? 'text-yellow-400' : ''}
                  ${machine.status === 'error' ? 'text-red-400' : ''}
                `} data-id="element-1026">
                  {machine.status === 'operational' ? 'Aktiv' : ''}
                  {machine.status === 'maintenance' ? 'Wartung' : ''}
                  {machine.status === 'error' ? 'Fehler' : ''}
                </span>
              </div>
            </td>
            <td className="py-3 pr-6" data-id="element-1027">
              <div className="flex items-center gap-2" data-id="element-1028">
                <span className="font-medium" data-id="element-1029">
                  {machine.metrics.efficiency}%
                </span>
                <div className="w-16 bg-gray-700 rounded-full h-1.5" data-id="element-1030">
                  <div className={`h-1.5 rounded-full ${machine.metrics.efficiency >= 80 ? 'bg-green-500' : machine.metrics.efficiency >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                width: `${machine.metrics.efficiency}%`
              }} data-id="element-1031"></div>
                </div>
              </div>
            </td>
            <td className="py-3 pr-6" data-id="element-1032">
              <div className="flex items-center gap-2" data-id="element-1033">
                <span className="font-medium" data-id="element-1034">
                  {machine.metrics.currentLoad}%
                </span>
                <div className="w-16 bg-gray-700 rounded-full h-1.5" data-id="element-1035">
                  <div className={`h-1.5 rounded-full ${machine.metrics.currentLoad >= 80 ? 'bg-blue-500' : machine.metrics.currentLoad >= 40 ? 'bg-blue-400' : 'bg-blue-300'}`} style={{
                width: `${machine.metrics.currentLoad}%`
              }} data-id="element-1036"></div>
                </div>
              </div>
            </td>
            <td className="py-3 pr-6 font-medium" data-id="element-1037">
              {formatNumber(calculateThroughput(machine))} Fässer/h
            </td>
            <td className="py-3 pr-6" data-id="element-1038">
              <span className={`${machine.metrics.warnings > 3 ? 'text-red-400' : machine.metrics.warnings > 0 ? 'text-yellow-400' : 'text-green-400'} font-medium`} data-id="element-1039">
                {machine.metrics.warnings}
              </span>
            </td>
            <td className="py-3" data-id="element-1040">
              <div className="flex items-center gap-2" data-id="element-1041">
                <span className={`${machine.analytics.errorRate > 3 ? 'text-red-400' : machine.analytics.errorRate > 1 ? 'text-yellow-400' : 'text-green-400'} font-medium`} data-id="element-1042">
                  {machine.analytics.errorRate}%
                </span>
              </div>
            </td>
          </tr>)}
      </tbody>
    </table>;
};